// @spec GHG-STR-108
/**
 * API client functions for Scope 1/2 activity assignment endpoints.
 */

import type {
  ActivityAssignmentConfirmRequest,
  ActivityAssignmentConfirmResponse,
  ActivityAssignmentFinalizeRequest,
  ActivityAssignmentFinalizeResponse,
  ActivityAssignmentJobCreateRequest,
  ActivityAssignmentJobResponse,
  ActivityAssignmentQueueFilters,
  ActivityAssignmentQueueListResponse,
  ActivityAssignmentSuggestRequest,
  ActivityAssignmentSuggestResponse,
} from '../types/activity-assignment';

import { createApiClient } from '@/lib/api-client';

export async function suggestActivityAssignment(
  getToken: () => Promise<string | null>,
  organizationId: string,
  data: ActivityAssignmentSuggestRequest
): Promise<ActivityAssignmentSuggestResponse> {
  const apiClient = createApiClient({ getAuthToken: getToken });
  return apiClient.post<ActivityAssignmentSuggestResponse>(
    `/organizations/${organizationId}/ghg/activity-assignment/suggest`,
    data
  );
}

export async function confirmActivityAssignment(
  getToken: () => Promise<string | null>,
  organizationId: string,
  data: ActivityAssignmentConfirmRequest
): Promise<ActivityAssignmentConfirmResponse> {
  const apiClient = createApiClient({ getAuthToken: getToken });
  return apiClient.post<ActivityAssignmentConfirmResponse>(
    `/organizations/${organizationId}/ghg/activity-assignment/confirm`,
    data
  );
}

export async function getActivityAssignmentQueue(
  getToken: () => Promise<string | null>,
  organizationId: string,
  filters: ActivityAssignmentQueueFilters
): Promise<ActivityAssignmentQueueListResponse> {
  const apiClient = createApiClient({ getAuthToken: getToken });

  const params = new URLSearchParams();
  if (filters.reportingPeriodId) params.set('reportingPeriodId', filters.reportingPeriodId);
  if (filters.scope !== undefined) params.set('scope', String(filters.scope));
  if (filters.status) params.set('status', filters.status);
  if (filters.factorStatus) params.set('factorStatus', filters.factorStatus);
  if (filters.facilityId) params.set('facilityId', filters.facilityId);
  if (filters.sourceImportId) params.set('sourceImportId', filters.sourceImportId);
  if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
  if (filters.createdTo) params.set('createdTo', filters.createdTo);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));

  const query = params.toString();
  const suffix = query ? `?${query}` : '';
  return apiClient.get<ActivityAssignmentQueueListResponse>(
    `/organizations/${organizationId}/ghg/activity-assignment/queue${suffix}`
  );
}

export async function createActivityAssignmentJob(
  getToken: () => Promise<string | null>,
  organizationId: string,
  data: ActivityAssignmentJobCreateRequest
): Promise<ActivityAssignmentJobResponse> {
  const apiClient = createApiClient({ getAuthToken: getToken });
  return apiClient.post<ActivityAssignmentJobResponse>(
    `/organizations/${organizationId}/ghg/activity-assignment/jobs`,
    data
  );
}

export async function getActivityAssignmentJob(
  getToken: () => Promise<string | null>,
  organizationId: string,
  jobId: string
): Promise<ActivityAssignmentJobResponse> {
  const apiClient = createApiClient({ getAuthToken: getToken });
  return apiClient.get<ActivityAssignmentJobResponse>(
    `/organizations/${organizationId}/ghg/activity-assignment/jobs/${jobId}`
  );
}

export async function finalizeActivityAssignmentQueueItem(
  getToken: () => Promise<string | null>,
  organizationId: string,
  itemId: string,
  data: ActivityAssignmentFinalizeRequest
): Promise<ActivityAssignmentFinalizeResponse> {
  const apiClient = createApiClient({ getAuthToken: getToken });
  return apiClient.post<ActivityAssignmentFinalizeResponse>(
    `/organizations/${organizationId}/ghg/activity-assignment/queue/${itemId}/finalize`,
    data
  );
}
